import type { SignInRefusalReason } from "./sign-in-refusal";

/**
 * How much sign-in traffic one address, one person, and one connection may use.
 *
 * The old rule counted every request from one connection against a single small
 * budget: twelve in a quarter of an hour. That number was chosen against a stranger
 * working through a list of addresses from a laptop, and it is right for that. It is
 * badly wrong for a convention hall, where two hundred teachers share one address on
 * the internet because the building has one. The thirteenth person to sign in would
 * have been turned away, and the four hundredth minute of the morning would still
 * have had people waiting.
 *
 * The fix is to stop counting requests and start counting outcomes. Somebody working
 * through a list produces almost nothing but misses, because the addresses are
 * guesses. A hall full of real teachers produces almost nothing but hits, because
 * each one is typing their own address. So misses get a tight budget and hits get a
 * loose one, and the two crowds stop sharing a limit they were never alike in.
 */
export const SIGN_IN_WINDOW_MS = 15 * 60 * 1_000;

/** The window in whole minutes, for telling somebody how long to wait. */
export const SIGN_IN_WINDOW_MINUTES = SIGN_IN_WINDOW_MS / 60_000;

/**
 * How many codes one address may ask for. Unchanged: three is enough for somebody
 * who deleted the first email and mistrusts the second.
 */
export const MAX_CODES_PER_PERSON = 3;

/**
 * How many addresses with no access one connection may ask about.
 *
 * This is the number that stops a roster being read off the sign-in form. Five is
 * generous for a person who mistyped their own address twice and tried a work
 * address before a personal one. It is useless to somebody holding a list, who needs
 * hundreds of guesses to learn anything and gets five.
 */
export const MAX_UNKNOWN_ADDRESSES_PER_SOURCE = 5;

/**
 * How many codes one connection may carry for people who do have access.
 *
 * Sized for the whole building rather than one desk: two hundred attendees, some
 * asking a second time, plus organizers and presenters, all sharing the venue's one
 * address. It still bounds the damage if the miss budget above is somehow walked
 * around, because every one of these had to match a real registration.
 */
export const MAX_CODES_PER_SOURCE = 400;

/**
 * How many sign-in squares one signed-in person may show.
 *
 * Ten was sized for somebody signing in their own phone and tablet. The registration
 * desk is the real use: an organizer standing in front of a queue, showing a square
 * to each person in turn. At ten per quarter of an hour that desk moves forty people
 * an hour, and a two-hundred-person convention takes five hours to get through the
 * door. One every seven seconds is a person working steadily, and it still bounds
 * what a stolen organizer session can hand out, because each square dies in two
 * minutes and signs in exactly one device.
 */
export const MAX_SQUARES_PER_PERSON = 120;

/**
 * How many times one address may be guessed at.
 *
 * Counted against the address rather than the connection, and that distinction is the
 * whole design. The venue shares one internet address, so counting refusals per
 * connection would let five teachers mistyping their passwords lock out the building —
 * a limit that fails exactly when the hall is fullest. Per address, a teacher who has
 * forgotten hers gets five goes and nobody else is touched, and somebody working through
 * one account gets five and stops.
 */
export const MAX_REFUSED_JOINS_PER_PERSON = 5;

/**
 * How many refusals one connection may produce, across every address.
 *
 * Loose on purpose. Two hundred teachers arriving at once will mistype between them, and
 * this must not be the thing that stops the morning. It is the second wall rather than
 * the first: the per-address budget above is what stops one account being guessed at,
 * and this only bounds somebody sweeping a list from a single connection.
 */
export const MAX_REFUSED_JOINS_PER_SOURCE = 100;

/**
 * How many people may get in through the join form on one connection.
 *
 * The same size as the code budget above and for the same reason: the whole building
 * shares one address on the first morning, and two hundred teachers joining inside a
 * quarter of an hour is the day working, not an attack.
 */
export const MAX_JOINS_PER_SOURCE = 400;

/**
 * The things worth counting. Each is a separate budget with its own key, so spending
 * one never spends another.
 */
export type SignInTally =
  /** An email-first entry decision that found no event registration. */
  | "entry_unknown"
  /** An email-first entry decision that found a registration. */
  | "entry_known"
  /** An address that has no access at this door. Counted against the connection. */
  | "unknown_address"
  /** A code sent to somebody who does have access. Counted against the connection. */
  | "code_to_known_address"
  /** A code sent to one person. Counted against that person. */
  | "code_for_person"
  /** A sign-in square shown by one signed-in person. */
  | "square_shown"
  /** A join the form turned away. Counted against the address being tried. */
  | "join_refused_for_person"
  /** A join the form turned away. Counted against the connection. */
  | "join_refused"
  /** A join that opened a session. Counted against the connection. */
  | "join_opened";

export interface SignInAllowance {
  /** How many are permitted inside the window. */
  limit: number;
  /** How long the window lasts. */
  windowMs: number;
  /** What the caller is told when this budget runs out. */
  refusal: SignInRefusalReason;
}

const ALLOWANCES: Readonly<Record<SignInTally, SignInAllowance>> = {
  entry_unknown: {
    limit: MAX_UNKNOWN_ADDRESSES_PER_SOURCE,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_from_source",
  },
  entry_known: {
    limit: MAX_CODES_PER_SOURCE,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_from_source",
  },
  join_refused_for_person: {
    limit: MAX_REFUSED_JOINS_PER_PERSON,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_for_address",
  },
  join_refused: {
    limit: MAX_REFUSED_JOINS_PER_SOURCE,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_from_source",
  },
  join_opened: {
    limit: MAX_JOINS_PER_SOURCE,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_from_source",
  },
  unknown_address: {
    limit: MAX_UNKNOWN_ADDRESSES_PER_SOURCE,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_from_source",
  },
  code_to_known_address: {
    limit: MAX_CODES_PER_SOURCE,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_from_source",
  },
  code_for_person: {
    limit: MAX_CODES_PER_PERSON,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "too_many_for_person",
  },
  square_shown: {
    limit: MAX_SQUARES_PER_PERSON,
    windowMs: SIGN_IN_WINDOW_MS,
    refusal: "square_too_many",
  },
};

export function signInAllowance(tally: SignInTally): SignInAllowance {
  return ALLOWANCES[tally];
}

/**
 * Whether a budget is spent. Takes the count of what has already happened inside the
 * window, not counting the request being decided, which is why this is `>=`.
 */
export function signInAllowanceSpent(tally: SignInTally, countInWindow: number): boolean {
  return countInWindow >= ALLOWANCES[tally].limit;
}

/**
 * How long somebody has to wait for a budget to free up, given when the oldest thing
 * inside the window happened. Returns whole minutes, rounded up, and never zero:
 * telling a person to wait no time at all reads as a fault rather than a limit.
 */
export function signInRetryAfterMinutes(
  tally: SignInTally,
  oldestInWindow: Date | null,
  now: Date,
): number {
  const { windowMs } = ALLOWANCES[tally];
  if (!oldestInWindow) return Math.ceil(windowMs / 60_000);
  const freesAt = oldestInWindow.getTime() + windowMs;
  const remaining = freesAt - now.getTime();
  if (!Number.isFinite(remaining) || remaining <= 0) return 1;
  return Math.max(1, Math.ceil(remaining / 60_000));
}
